/**
 * Document validation. Messages are user-facing, hence French; identifiers and
 * comments stay English per the workspace convention.
 */

import type { Band, ChronoDocument, Item } from './types';
import { isPeriod } from './types';

/**
 * A band must not carry more ticks than fit legibly across the drawing area: the
 * usable A4 landscape width less the band-name gutter, 986 px ÷ 65 ≈ 15.2 px per
 * tick (implementation-plan.md §5.1). Beyond that the step is too fine for the
 * span covered, and the editor blocks rather than emitting an unreadable band.
 *
 * The cap was set when the band name sat on a line above the chart and the whole
 * 1062 px was drawing area, which made 65 a conservative figure. Moving the name
 * into the gutter turned it into the exact one: it now lands a fifth of a pixel
 * above `MIN_TICK_PX`, so there is no slack left to spend.
 */
export const MAX_TICKS_PER_BAND = 65;

/**
 * Number of ticks actually drawn: the regular ones, plus the forced tick on the
 * end bound when the span is not a whole multiple of the step (features.md §2 —
 * both band bounds are always shown).
 *
 * The prototype divided span by step, which counts intervals rather than ticks
 * and under-counts by one.
 */
export function tickCount(band: Band): number {
  const span = band.end - band.start;
  const whole = Math.floor(span / band.step);
  return whole + 1 + (span % band.step === 0 ? 0 : 1);
}

export function validateBands(bands: readonly Band[]): string[] {
  const errors: string[] = [];

  bands.forEach((band, i) => {
    const where = `Bande ${i + 1} ("${band.label}")`;

    if (!Number.isFinite(band.step) || band.step <= 0) {
      errors.push(`${where} : le pas doit être un nombre strictement positif (reçu ${band.step}).`);
      return;
    }
    if (band.end <= band.start) {
      errors.push(`${where} : la fin (${band.end}) doit être postérieure au début (${band.start}).`);
      return;
    }

    const ticks = tickCount(band);
    if (ticks > MAX_TICKS_PER_BAND) {
      errors.push(
        `${where} : ${band.start} → ${band.end} au pas de ${band.step} donne ${ticks} graduations, ` +
          `maximum ${MAX_TICKS_PER_BAND}. Choisis un pas plus grossier ou découpe cette période en plusieurs bandes.`,
      );
    }
  });

  return errors;
}

export function validateItems(items: readonly Item[], categories: ChronoDocument['categories']): string[] {
  const errors: string[] = [];

  items.forEach((item, i) => {
    const where = `Entrée ${i + 1} ("${item.label}")`;

    if (!(item.cat in categories)) {
      errors.push(`${where} : catégorie inconnue "${item.cat}".`);
    }

    if (isPeriod(item)) {
      if (item.end < item.start) {
        errors.push(`${where} : la fin (${item.end}) précède le début (${item.start}).`);
      }
      return;
    }

    if (item.span) {
      const [from, to] = item.span;
      if (to < from) {
        errors.push(`${where} : fourchette inversée (${from} → ${to}).`);
      } else if (item.year < from || item.year > to) {
        errors.push(`${where} : l'année ${item.year} est hors de sa fourchette d'incertitude (${from} → ${to}).`);
      }
    }
  });

  return errors;
}

export function validateDocument(doc: ChronoDocument): string[] {
  return [...validateBands(doc.bands), ...validateItems(doc.items, doc.categories)];
}
